import AppPortalApi from '@/api/AppPortalApi';
import router from '../router/index';
import store from '../store/index';

export default {
  // User Permissions Management
  async getAllUserPermissions(tenantId: string): Promise<any> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }

    const response = await AppPortalApi.getAllUserPermissions(tenantId);
    const userPermissions: any[] = [];

    if (response.data && response.data.userPermissions) {
      response.data.userPermissions.forEach((permission: any) => {
        userPermissions.push({
          tenantId: permission.tenantId,
          userId: permission.userId,
          dashboardCreate: permission.dashboardCreate,
          dashboardShare: permission.dashboardShare,
          propertyFavorites: permission.propertyFavorites,
          staging: permission.staging,
          canvasWrite: permission.canvasWrite,
        });
      });
    }

    return userPermissions;
  },

  async getUserPermissions(tenantId: string, userId: string): Promise<any> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }

    const response = await AppPortalApi.getUserPermissions(tenantId, userId);

    return {
      tenantId: response.data.tenantId,
      userId: response.data.userId,
      dashboardCreate: response.data.dashboardCreate,
      dashboardShare: response.data.dashboardShare,
      propertyFavorites: response.data.propertyFavorites,
      staging: response.data.staging,
      canvasWrite: response.data.canvasWrite,
    };
  },

  async createUserPermissions(
    tenantId: string,
    userId: string,
    dashboardCreate: boolean,
    dashboardShare: boolean,
    propertyFavorites: boolean,
    staging: boolean,
    canvasWrite: boolean
  ): Promise<any> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }

    const response = await AppPortalApi.createUserPermissions(
      tenantId,
      userId,
      dashboardCreate,
      dashboardShare,
      propertyFavorites,
      staging,
      canvasWrite
    );

    if (
      response.data &&
      response.data.success &&
      response.data.userPermission
    ) {
      return {
        tenantId: response.data.userPermission.tenantId,
        userId: response.data.userPermission.userId,
        dashboardCreate: response.data.userPermission.dashboardCreate,
        dashboardShare: response.data.userPermission.dashboardShare,
        propertyFavorites: response.data.userPermission.propertyFavorites,
        staging: response.data.userPermission.staging,
        canvasWrite: response.data.userPermission.canvasWrite,
      };
    } else {
      throw new Error('Failed to create user permissions');
    }
  },

  async updateUserPermissions(
    tenantId: string,
    userId: string,
    permissions: Partial<{
      dashboardCreate: boolean;
      dashboardShare: boolean;
      propertyFavorites: boolean;
      staging: boolean;
      canvasWrite: boolean;
    }>
  ): Promise<any> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }

    const response = await AppPortalApi.updateUserPermissions(
      tenantId,
      userId,
      permissions
    );

    if (
      response.data &&
      response.data.success &&
      response.data.userPermission
    ) {
      return {
        tenantId: response.data.userPermission.tenantId,
        userId: response.data.userPermission.userId,
        dashboardCreate: response.data.userPermission.dashboardCreate,
        dashboardShare: response.data.userPermission.dashboardShare,
        propertyFavorites: response.data.userPermission.propertyFavorites,
        staging: response.data.userPermission.staging,
        canvasWrite: response.data.userPermission.canvasWrite,
      };
    } else {
      throw new Error('Failed to update user permissions');
    }
  },

  async deleteUserPermissions(tenantId: string, userId: string): Promise<void> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }

    await AppPortalApi.deleteUserPermissions(tenantId, userId);
  },

  // Tenant Default Permissions Management
  async getTenantDefaultPermissions(tenantId: string): Promise<any> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }

    const response = await AppPortalApi.getTenantDefaultPermissions(tenantId);

    return {
      tenantId: response.data.tenantId,
      dashboardCreate: response.data.dashboardCreate,
      dashboardShare: response.data.dashboardShare,
      propertyFavorites: response.data.propertyFavorites,
      staging: response.data.staging,
      canvasWrite: response.data.canvasWrite,
    };
  },

  async createTenantDefaultPermissions(
    tenantId: string,
    dashboardCreate: boolean,
    dashboardShare: boolean,
    propertyFavorites: boolean,
    staging: boolean,
    canvasWrite: boolean
  ): Promise<any> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }

    const response = await AppPortalApi.createTenantDefaultPermissions(
      tenantId,
      dashboardCreate,
      dashboardShare,
      propertyFavorites,
      staging,
      canvasWrite
    );

    if (
      response.data &&
      response.data.success &&
      response.data.tenantDefaultPermissions
    ) {
      return {
        tenantId: response.data.tenantDefaultPermissions.tenantId,
        dashboardCreate: response.data.tenantDefaultPermissions.dashboardCreate,
        dashboardShare: response.data.tenantDefaultPermissions.dashboardShare,
        propertyFavorites:
          response.data.tenantDefaultPermissions.propertyFavorites,
        staging: response.data.tenantDefaultPermissions.staging,
        canvasWrite: response.data.tenantDefaultPermissions.canvasWrite,
      };
    } else {
      throw new Error('Failed to create tenant default permissions');
    }
  },

  async updateTenantDefaultPermissions(
    tenantId: string,
    permissions: Partial<{
      dashboardCreate: boolean;
      dashboardShare: boolean;
      propertyFavorites: boolean;
      staging: boolean;
      canvasWrite: boolean;
    }>
  ): Promise<any> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }

    const response = await AppPortalApi.updateTenantDefaultPermissions(
      tenantId,
      permissions
    );

    if (
      response.data &&
      response.data.success &&
      response.data.tenantDefaultPermissions
    ) {
      return {
        tenantId: response.data.tenantDefaultPermissions.tenantId,
        dashboardCreate: response.data.tenantDefaultPermissions.dashboardCreate,
        dashboardShare: response.data.tenantDefaultPermissions.dashboardShare,
        propertyFavorites:
          response.data.tenantDefaultPermissions.propertyFavorites,
        staging: response.data.tenantDefaultPermissions.staging,
        canvasWrite: response.data.tenantDefaultPermissions.canvasWrite,
      };
    } else {
      throw new Error('Failed to update tenant default permissions');
    }
  },

  async deleteTenantDefaultPermissions(tenantId: string): Promise<void> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }

    await AppPortalApi.deleteTenantDefaultPermissions(tenantId);
  },

  async getOwnUserPermissions(tenantId: string): Promise<any> {
    if (!store.getters.loggedIn(Date.now())) {
      router.push('/login');
    }
    const response = await AppPortalApi.getOwnUserPermissions(tenantId);

    return {
      tenantId: response.data.tenantId,
      userId: response.data.userId,
      dashboardCreate: response.data.permissions.dashboardCreate,
      dashboardShare: response.data.permissions.dashboardShare,
      propertyFavorites: response.data.permissions.propertyFavorites,
      staging: response.data.permissions.staging,
      canvasWrite: response.data.permissions.canvasWrite,
    };
  },

  // Utility methods
  getPermissionTypes(): Array<{
    key: string;
    label: string;
    description: string;
  }> {
    return [
      {
        key: 'dashboardCreate',
        label: 'Dashboard Create',
        description: 'Allow user to create new dashboards',
      },
      {
        key: 'dashboardShare',
        label: 'Dashboard Share',
        description: 'Allow user to share dashboards with others',
      },
      {
        key: 'propertyFavorites',
        label: 'Property Favorites',
        description: 'Allow user to manage property favorites',
      },
      {
        key: 'staging',
        label: 'Staging Access',
        description: 'Allow user to access staging features',
      },
      {
        key: 'canvasWrite',
        label: 'Canvas Write',
        description: 'Allow user to edit canvas data',
      },
    ];
  },
};
